import { useState } from "react";
import { motion } from "framer-motion";
import { providerImage } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useCompare } from "@/context/CompareContext";
import { MapPin, ShieldCheck, Star, ExternalLink, Check, GitCompare } from "lucide-react";

const money = (n) => `$${Number(n).toLocaleString()}`;
export const startingPrice = (p) =>
  p.starting_price ?? p.min_price ?? (p.packages?.length ? Math.min(...p.packages.map((k) => k.price)) : null);

const CATEGORY_GRADIENT = {
  "Surgical": "linear-gradient(135deg, #0D5C75, #2A9D8F)",
  "Clinical & Diagnostics": "linear-gradient(135deg, #1E40AF, #3B82F6)",
  "Rehabilitation & Recovery": "linear-gradient(135deg, #065F46, #10B981)",
  "Wellness & Alternative": "linear-gradient(135deg, #92400E, #D97706)",
  "Specialized Care": "linear-gradient(135deg, #5B21B6, #8B5CF6)",
  "Women & Children": "linear-gradient(135deg, #9D174D, #EC4899)",
};
const gradientFor = (p) => CATEGORY_GRADIENT[p.category] || CATEGORY_GRADIENT["Surgical"];

function AccreditationBadge({ name }) {
  const jci = name === "JCI";
  return (
    <Badge className={`border-0 text-xs font-semibold ${jci ? "bg-[#D4A017] text-white" : "bg-[#1D4ED8] text-white"}`}
      data-testid={`accreditation-badge-${name.toLowerCase()}`}>
      <ShieldCheck size={11} className="mr-1" />{name}
    </Badge>
  );
}

export default function ProviderCard({ p, i = 0, onView, showCompare = true }) {
  const [imgFailed, setImgFailed] = useState(false);
  const compare = useCompare();
  const selected = compare?.isSelected(p.id);
  const price = startingPrice(p);
  const specs = p.specialties || [];
  const hasImages = (p.images || []).length > 0;
  const hero = hasImages ? providerImage(p.id, 0) : null;
  const showImage = hero && !imgFailed;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
      className={`group card-modern tricolor-top overflow-hidden flex flex-col ${p.featured ? "ring-2 ring-[#F97316]" : ""} ${selected ? "ring-2 ring-[#0D5C75]" : ""}`}
      data-testid={`directory-provider-${p.id}`}>
      <div className="relative h-48 w-full overflow-hidden" style={{ minHeight: 200, background: gradientFor(p) }}
        data-testid={showImage ? undefined : `provider-placeholder-${p.id}`}>
        {showImage ? (
          <>
            <img src={hero} alt={`${p.name} facility`} loading="lazy" decoding="async"
              onError={() => setImgFailed(true)}
              className="mv-zoom-img absolute inset-0 h-full w-full object-cover bg-[#F3F4F6]" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
          </>
        ) : (
          <div className="absolute inset-0 grid place-items-center px-4 text-center">
            <div>
              <div className="font-heading font-extrabold text-white text-lg leading-tight drop-shadow">{p.name}</div>
              <div className="text-white/85 text-xs mt-1">{p.city}, India</div>
            </div>
          </div>
        )}
        <div className="absolute top-2.5 left-2.5 flex flex-wrap gap-1.5 max-w-[60%]">
          {(p.accreditation || []).map((a) => <AccreditationBadge key={a} name={a} />)}
        </div>
        <div className="absolute top-2.5 right-2.5 flex flex-col items-end gap-1.5">
          {showCompare && compare && (
            <button type="button" onClick={(e) => { e.stopPropagation(); compare.toggle(p.id); }}
              data-testid={`compare-checkbox-${p.id}`} aria-pressed={selected}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm transition-colors ${selected ? "bg-[#0D5C75] text-white" : "bg-white/95 text-[#0D5C75] hover:bg-white"}`}>
              <span className={`grid place-items-center h-4 w-4 rounded-[4px] border ${selected ? "bg-white border-white" : "border-[#0D5C75]"}`}>
                {selected && <Check size={12} className="text-[#0D5C75]" />}
              </span>
              Compare
            </button>
          )}
          {p.featured && <Badge className="bg-[#F97316] text-white border-0" data-testid={`featured-badge-${p.id}`}>Featured</Badge>}
          {p.tier === "premium" && <Badge className="bg-[#FBBF24] text-[#101828] border-0">Premium</Badge>}
          {p.tier === "enterprise" && <Badge className="bg-[#8B5CF6] text-white border-0">Enterprise</Badge>}
        </div>
        {showImage && (
          <div className="absolute bottom-2.5 left-3 right-3">
            <h3 className="font-heading font-bold text-lg text-white leading-tight drop-shadow" data-testid={`provider-name-${p.id}`}>{p.name}</h3>
            <p className="text-xs text-white/90 flex items-center gap-1 mt-0.5"><MapPin size={12} /> {p.city}, India</p>
          </div>
        )}
      </div>

      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-1.5">
          <Star size={15} fill="#D97706" className="text-[#D97706]" />
          <span className="font-semibold text-sm text-[#101828]">{p.rating}</span>
          {p.success_rate != null && <span className="text-xs text-muted-foreground">· {p.success_rate}% success rate</span>}
        </div>

        <div className="flex flex-wrap gap-1.5 mt-3">
          {specs.slice(0, 3).map((s) => (
            <Badge key={s} variant="outline" className="border-[#0D5C75]/25 bg-[#E9F5F9] text-[#0D5C75] text-xs">{s}</Badge>
          ))}
          {specs.length > 3 && (
            <Badge variant="outline" className="border-[#E4E7EC] text-muted-foreground text-xs" data-testid={`more-specialties-${p.id}`}>+{specs.length - 3} more</Badge>
          )}
        </div>

        {price != null && (
          <div className="text-sm text-muted-foreground mt-3" data-testid={`price-${p.id}`}>From{" "}
            <span className="font-heading font-bold text-[#0D5C75] text-base">{money(price)}</span>
          </div>
        )}

        <div className="mt-auto pt-4 flex items-center gap-3">
          <Button type="button" className="flex-1 rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white"
            onClick={() => onView(p)} data-testid={`view-profile-${p.id}`}>View Profile</Button>
          {showCompare && compare && (
            <Button type="button" variant="outline" size="icon"
              className={`rounded-full shrink-0 ${selected ? "border-[#0D5C75] text-[#0D5C75] bg-[#E9F5F9]" : ""}`}
              onClick={() => compare.toggle(p.id)} title="Add to compare" data-testid={`compare-btn-${p.id}`}>
              <GitCompare size={16} />
            </Button>
          )}
        </div>
        {p.website_url && (
          <a href={p.website_url} target="_blank" rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="mt-2 self-end inline-flex items-center gap-1 text-xs text-[#2A9D8F] hover:underline"
            data-testid={`official-site-${p.id}`}>
            Official Site <ExternalLink size={12} />
          </a>
        )}
      </div>
    </motion.div>
  );
}
